import { applyI18n, t } from '../../i18n';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';

export interface SedrEditLinkTypeDialogOptions {
  mode: 'create' | 'edit';
  initialName?: string;
  initialInverseName?: string | null;
}

export interface SedrEditLinkTypeDialogResult {
  name: string;
  inverseName: string | null;
}

/**
 * Modal form to create or edit a custom requirement link type.
 * Returns the submitted values, or null if cancelled.
 */
export async function showSedrEditLinkTypeDialog(
  options: SedrEditLinkTypeDialogOptions,
): Promise<SedrEditLinkTypeDialogResult | null> {
  return new Promise((resolve) => {
    const shell = createModalShell({
      titleKey: options.mode === 'create' ? 'sedr.links.addCustomType' : 'sedr.links.editTypeTitle',
      className: 'sedr-edit-link-type-dialog',
    });

    shell.body.innerHTML = `
      <p class="muted" data-i18n="sedr.links.editTypeHint"></p>
      <form class="form-grid" id="sedr-edit-link-type-form">
        <label>
          <span data-i18n="sedr.links.typeName"></span>
          <input type="text" name="name" required maxlength="120" autocomplete="off" />
        </label>
        <label>
          <span data-i18n="sedr.links.inverseName"></span>
          <input
            type="text"
            name="inverseName"
            maxlength="120"
            autocomplete="off"
            data-i18n-placeholder="sedr.links.inverseNameHint"
          />
        </label>
        <p class="form-error" id="sedr-edit-link-type-error" hidden></p>
      </form>
    `;

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'button ghost';
    cancelButton.setAttribute('data-i18n', 'common.cancel');

    const submitButton = document.createElement('button');
    submitButton.type = 'submit';
    submitButton.className = 'button primary';
    submitButton.setAttribute('form', 'sedr-edit-link-type-form');
    submitButton.setAttribute(
      'data-i18n',
      options.mode === 'create' ? 'sedr.links.saveType' : 'sedr.links.saveTypeEdit',
    );

    shell.actions.append(cancelButton, submitButton);
    document.body.append(shell.backdrop);
    applyI18n(shell.dialog);

    const form = shell.body.querySelector('#sedr-edit-link-type-form') as HTMLFormElement;
    const errorEl = shell.body.querySelector('#sedr-edit-link-type-error') as HTMLParagraphElement;
    const nameInput = form.querySelector('input[name="name"]') as HTMLInputElement;
    const inverseInput = form.querySelector('input[name="inverseName"]') as HTMLInputElement;
    nameInput.value = options.initialName ?? '';
    inverseInput.value = options.initialInverseName ?? '';

    const finish = (value: SedrEditLinkTypeDialogResult | null) => {
      unbindDismiss();
      shell.backdrop.remove();
      resolve(value);
    };

    const unbindDismiss = bindModalDismiss({
      backdrop: shell.backdrop,
      closeButton: shell.closeButton,
      onClose: () => finish(null),
    });

    cancelButton.addEventListener('click', () => finish(null));
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      errorEl.hidden = true;
      const name = nameInput.value.trim();
      if (!name) {
        errorEl.hidden = false;
        errorEl.textContent = t('sedr.error.linkTypeNameRequired');
        nameInput.focus();
        return;
      }
      const inverseName = inverseInput.value.trim();
      finish({
        name,
        inverseName: inverseName || null,
      });
    });

    nameInput.focus();
    if (options.mode === 'edit') {
      nameInput.select();
    }
  });
}
